import { useState, useCallback, useMemo } from 'react'
import { apiGet } from '../../../../../generic/Api_Functions'
import useMosInfo from '../../../common/provider/useMosInfo'

/**
 * Hook to load and manage the incidence catalog (types and incidences).
 * @returns {{
 *   catalog: Array,
 *   loading: boolean,
 *   error: Error|null,
 *   loadCatalog: Function,
 *   incidenceTypes: Array,
 *   getIncidencesByType: Function
 * }} Catalog state and computed options.
 */
export const useIncidenceCatalog = () => {
  const { device } = useMosInfo()
  const [catalog, setCatalog] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Loads the incidence catalog for the current device.
   * @returns {Promise<void>}
   */
  const loadCatalog = useCallback(async () => {
    if (!device?.id) return
    setLoading(true)
    setError(null)
    try {
      const res = await apiGet('incidencesByDeviceAndDeviceStatus/' + device.id)
      setCatalog(Array.isArray(res) ? res : [])
    } catch (e) {
      console.error('Error loading catalog:', e)
      setCatalog([])
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [device?.id])

  const incidenceTypes = useMemo(() => {
    const byId = new Map()
    for (const incidenceItem of catalog) {
      const incidenceType = incidenceItem?.incidenceType
      if (incidenceType?.id && !byId.has(incidenceType.id)) {
        byId.set(incidenceType.id, incidenceType)
      }
    }
    return Array.from(byId.values())
  }, [catalog])

  const incidenceTypeIdByName = useMemo(() => {
    const map = new Map()
    for (const incidenceType of incidenceTypes) {
      if (incidenceType?.name && incidenceType?.id) {
        map.set(incidenceType.name, incidenceType.id)
      }
    }
    return map
  }, [incidenceTypes])

  /**
   * Returns the incidences belonging to a given type name.
   * @param {string} typeName
   * @returns {Array}
   */
  const getIncidencesByType = useCallback((typeName) => {
    if (!typeName) return []
    const typeId = incidenceTypeIdByName.get(typeName)
    if (!typeId) return []
    return catalog.filter((incidenceItem) => incidenceItem?.incidenceType?.id === typeId)
  }, [catalog, incidenceTypeIdByName])

  return {
    catalog,
    loading,
    error,
    loadCatalog,
    incidenceTypes,
    getIncidencesByType
  }
}
